import React, { useState } from "react";
import { Button, makeStyles } from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../../translate/i18n";
import api from "../../../services/api";
import IconFieldAndFolder from '../../../assets/miscPng/file_and_folder.png';
import LocationPreview from "../../LocationPreview";
import MarkdownWrapper from "../../MarkdownWrapper";
import saveFile from "../../../utils/saveFile";
import toastError from "../../../errors/toastError";

import DefaultValuesUtils from "../../../utils/classes/DefaultValuesUtils";
import MessageUtils from "../../../utils/classes/MessageUtils";

//  ************
//  ** Styles **
//  ************
const useStyles = makeStyles((theme) => ({
  titleSpan: {
    fontWeight: "bold"
    , fontSize: "16px"
  }

  , footerSpan: {
    fontSize: "14px"
    , color: "#959EAC"
  }

  , templateButton: {
    color: theme.palette.secondary.ackCheck
    , borderColor: theme.palette.secondary.ackCheck
  }

  , floatingButton: {
    transition: "transform 0.3s"
    , "&:hover": { transform: "translateY(-5px)" }
  }

  , subMessage: {
    objectFit: "cover"
    , transition: "all 0.30s"
    , width: "250px"
    , height: "200px"
    , borderTopLeftRadius: "8px"
    , borderTopRightRadius: "8px"
    , borderBottomLeftRadius: "8px"
    , borderBottomRightRadius: "8px"
  }

  , subMessageDeleted: {
    opacity: 0.70
    , filter: "grayscale(80%)"
  }

  , subMessageDocumentContainer: {
    display: "flex"
    , flexDirection: "column"
    , justifyContent: "center"
    , alignItems: "center"
    , gap: "1em"
  }
}));

//  ****************
//  ** Components **
//  ****************
const TemplateSubMessage = ({ type, mediaUrl, body, isDeleted }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const className = isDeleted ? `${classes.subMessage} ${classes.subMessageDeleted}` : `${classes.subMessage}`;

  const subTypeMedias = [
    DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.document
    , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
    , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video
  ];

  const subTypeContents = [
    DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
  ];



  //  ***************
  //  ** Functions **
  //  ***************
  const renderDocument = () => {
    const handleClick = (event) => {
      event.preventDefault();
      saveFile({ mediaUrl, body });
    };

    return (
      <div className={classes.subMessageDocumentContainer}>
        <img className={className} alt="Sub Message Document" src={IconFieldAndFolder} />

        <Button
          startIcon={<GetApp />}
          color="inherit"
          variant="outlined"
          target="_blank"
          href="#"
          onClick={(event) => handleClick(event)}
          className={classes.floatingButton}
        >
          {i18n.t("messagesInput.download")}
        </Button>
      </div>
    );
  };

  const renderImage = () => {
    return (
      <img className={className} alt="Template Sub Message" src={mediaUrl} />
    );
  };

  const renderVideo = () => {
    return (
      <video className={className} src={mediaUrl} controls={true} />
    );
  };

  const renderLocation = () => {
    const isLocationValid = body.split("|").length >= 2;
    if (!isLocationValid) return (<></>);

    const { imageLocation, linkLocation, descriptionLocation } = MessageUtils.extractLocationInfo({ body });

    return (
      <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    );
  };

  const renderSubMessage = () => {
    if (!type) return (<></>);
    if (!subTypeMedias.includes(type) && !subTypeContents.includes(type)) return (<></>);
    if (subTypeMedias.includes(type) && !mediaUrl) return (<></>);
    if (subTypeContents.includes(type) && !body) return (<></>);

    if (type === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.document) return renderDocument();
    if (type === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image) return renderImage();
    if (type === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video) return renderVideo();
    return renderLocation();
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <center>
      {renderSubMessage()}
    </center>
  );
};

const TemplateTexts = ({ title, body }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();



  //  ************
  //  ** Return **
  //  ************
  return (
    <>
      {title && (<span className={classes.titleSpan}>{title}</span>)}
      {body && (<MarkdownWrapper>{body}</MarkdownWrapper>)}
    </>
  );
};

const TemplateButtons = ({ buttons, message, ticketId, isMinifiedPreview }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);



  //  ***************
  //  ** Functions **
  //  ***************

  // ***---- Renders ----***
  const renderGenericButton = (type, onClick, label, isDeleted, isInteracted) => {
    const onClickFunction = onClick.constructor.name === "AsyncFunction"
      ? async () => await onClick()
      : onClick;

    const isButtonDisabled = ["callButton", "quickReplyButton"].includes(type) 
      ? isDeleted || isMinifiedPreview || isInteracted || isSubmitting
      : isDeleted;

    return (
      <Button
        className={`${classes.templateButton} ${classes.floatingButton}`}
        variant="outlined"
        disabled={isButtonDisabled}
        onClick={onClickFunction}
      >
        {label || i18n.t("template.buttons.defaultText")}
      </Button>
    );
  };

  // ***---- Click Handlers ----***
  const handleCallButtonClick = (button) => {
    toast.info(i18n.t("template.toasts.featureNotAvailable"));
  };

  const handleUrlButtonClick = async (url) => {
    if (!url) toast.info(i18n.t("template.toasts.urlForButtonNotAvailable"));

    const urlInstance = new URL(url);
    const baseUrl = `${urlInstance.origin}${urlInstance.pathname}`;
    
    if (baseUrl === DefaultValuesUtils.URLS.officialApi.coupon) {
      const coupon = urlInstance.searchParams.get("code");

      if (coupon) {
        await navigator.clipboard.writeText(coupon);
        toast.success(i18n.t("template.toasts.couponCodeCopied"));
      }
      else {
        toast.info(i18n.t("template.toasts.couponCodeNotAvailable"));
      }
    }
    else {
      window.open(url);
    }
  };

  const handleQuickReplyButtonClick = async (button, isInteracted, ticketId) => {
    try {
      if (isInteracted || isMinifiedPreview) return;
      setIsSubmitting(true);

      const selectedButtonIndex = button.index;
      if (!selectedButtonIndex || selectedButtonIndex === -1) toast.info(i18n.t("template.toasts.quickReplyIdNotAvailable"));

      const selectedButtonId = button.quickReplyButton.id;
      if (!selectedButtonId) toast.info(i18n.t("template.toasts.quickReplyIdNotAvailable"));

      await api.post(`/messagesTemplateButtonReply/${ticketId}`, {
        selectedButtonIndex: selectedButtonIndex
        , selectedButtonId: selectedButtonId
        , body: button.quickReplyButton.displayText ?? i18n.t("template.buttons.defaultText")
        , quotedMsgId: message.id
      });
    }
    catch (exception) {
      console.log("handleQuickReplyButtonClick Exception:", exception);
      toastError(exception);
    }
    finally {
      setIsSubmitting(false);
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    buttons.map(button => {
      // ***---- Hydrated Template Buttons ----***
      if (button.callButton) {
        return renderGenericButton(
          "callButton"
          , () => handleCallButtonClick(button)
          , button.callButton.displayText
          , message.isDeleted
          , message.isInteracted
        );
      }

      if (button.urlButton) {
        return renderGenericButton(
          "urlButton"
          , async () => await handleUrlButtonClick(button.urlButton.url)
          , button.urlButton.displayText
          , message.isDeleted
          , message.isInteracted
        );
      }

      if (button.quickReplyButton) {
        return renderGenericButton(
          "quickReplyButton"
          , async () => await handleQuickReplyButtonClick(button, message.isInteracted, ticketId)
          , button.quickReplyButton.displayText
          , message.isDeleted
          , message.isInteracted
        );
      }

      // ***---- Interactive Template Buttons ----***
      if (button.ctaUrlButton) {
        return renderGenericButton(
          "ctaUrlButton"
          , async () => await handleUrlButtonClick(button.ctaUrlButton.url)
          , button.ctaUrlButton.displayText
          , message.isDeleted
          , message.isInteracted
        );
      }

      // ***---- Unknown Button ----***
      return (<></>);
    })
  );
};

const TemplateFooter = ({ footer }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();



  //  ************
  //  ** Return **
  //  ************
  return (<span className={classes.footerSpan}>{footer}</span>);
};

//  ************
//  ** Return **
//  ************
export { TemplateSubMessage, TemplateTexts, TemplateButtons, TemplateFooter };
